/**
 * Parse + validate an imported `TemplateConfig` JSON string.
 *
 * Import is an untrusted boundary: a hand-edited or stale file could be
 * malformed, the wrong version, or a completely different shape. We validate the
 * structure BEFORE handing it to `LOAD_TEMPLATE` so a bad file is rejected with a
 * message instead of corrupting the editor. This is a structural shape check
 * (enough to render + edit safely), not an exhaustive schema validator — the
 * goal is "won't crash the reducer/canvas", and unknown extra fields are
 * tolerated.
 *
 * The same validator is reused to vet the template loaded from the backend on
 * open (see `getTemplate` in `services/api/templates.ts`) and the preview-tab
 * handoff snapshot (see `previewHandoff.ts`).
 */

import {
  LayoutGrid,
  Placement,
  Region,
  RegionKind,
  Section,
  TemplateConfig,
} from '../model/types';

/** The kinds a region/content discriminant may legally take. */
const REGION_KINDS: ReadonlyArray<RegionKind> = [
  'header',
  'footer',
  'leftToc',
  'rightSubToc',
  'main',
  'faq',
  'custom',
  'title',
  'dates',
  'prolog',
  'search',
];

/** Result of validating an untrusted value as a `TemplateConfig`. */
export type ImportResult =
  | { ok: true; config: TemplateConfig }
  | { ok: false; error: string };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isTrack(value: unknown): boolean {
  return isRecord(value) && typeof value.size === 'string';
}

function isLayout(value: unknown): value is LayoutGrid {
  if (!isRecord(value)) return false;
  const columnsOk = Array.isArray(value.columns) && value.columns.length > 0 && value.columns.every(isTrack);
  const rowsOk = Array.isArray(value.rows) && value.rows.length > 0 && value.rows.every(isTrack);
  return columnsOk && rowsOk && typeof value.gap === 'string';
}

function isPlacement(value: unknown): value is Placement {
  if (!isRecord(value)) return false;
  return (
    typeof value.colStart === 'number' &&
    typeof value.colSpan === 'number' &&
    typeof value.rowStart === 'number' &&
    typeof value.rowSpan === 'number'
  );
}

function isRegion(value: unknown): value is Region {
  if (!isRecord(value)) return false;
  if (typeof value.id !== 'string' || typeof value.label !== 'string') return false;
  if (typeof value.visible !== 'boolean') return false;
  if (REGION_KINDS.indexOf(value.kind as RegionKind) === -1) return false;
  // placement is OPTIONAL (chrome omits it, or carries null): required only for
  // section elements. When present it must be a valid `Placement`.
  if (value.placement != null && !isPlacement(value.placement)) return false;
  if (!isRecord(value.style)) return false;
  // Content must exist and its discriminant must match the region kind.
  if (!isRecord(value.content) || value.content.kind !== value.kind) return false;
  return true;
}

/** A body section: id + its own grid + placed elements. */
function isSection(value: unknown): value is Section {
  if (!isRecord(value)) return false;
  if (typeof value.id !== 'string') return false;
  if (!isLayout(value.grid)) return false;
  return Array.isArray(value.elements) && value.elements.every(isRegion);
}

/** Validate an already-parsed value as a renderable, editable v2 `TemplateConfig`. */
export function validateTemplate(value: unknown): ImportResult {
  if (!isRecord(value)) {
    return { ok: false, error: 'File is not a template object.' };
  }
  if (value.version !== 2) {
    return { ok: false, error: 'Unsupported template version. Expected version 2.' };
  }
  if (typeof value.name !== 'string') {
    return { ok: false, error: 'Template is missing a name.' };
  }
  if (!isRecord(value.theme)) {
    return { ok: false, error: 'Template is missing theme tokens.' };
  }
  if (!Array.isArray(value.sections) || !value.sections.every(isSection)) {
    return { ok: false, error: 'Template sections are missing or malformed.' };
  }
  for (const key of ['header', 'footer', 'faq'] as const) {
    if (value[key] != null && !isRegion(value[key])) {
      return { ok: false, error: `Template ${key} chrome is malformed.` };
    }
  }
  return { ok: true, config: value as unknown as TemplateConfig };
}

/** Parse a JSON string then validate it as a `TemplateConfig`. */
export function parseTemplate(json: string): ImportResult {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return { ok: false, error: 'File is not valid JSON.' };
  }
  return validateTemplate(parsed);
}

/** Read a `File` as text and validate it as a `TemplateConfig`. */
export async function readTemplateFile(file: File): Promise<ImportResult> {
  try {
    const text = await file.text();
    return parseTemplate(text);
  } catch {
    return { ok: false, error: 'Could not read the selected file.' };
  }
}
